/**
 * Which hotel brand the admin shell should wear for the current route.
 *  - /locations/:id  → that property's directory brand
 *  - /customers/:id  → the customer's first branded property
 *  - anything else   → none (PazNetworks look)
 * Mirrors the customer portal (pickViewBrand in @pazportal/ui).
 */
import { useMemo } from 'react';
import { useMatch } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { pickViewBrand } from '@pazportal/ui';
import { supabase } from '../lib/supabase';
import { propertyCodeOf } from './usePropertyDirectory';

interface ShellLocation {
  id: string;
  name: string;
  directory: { brand: string | null } | null;
}

export function useShellBrand(pathname: string, customerName: string | null | undefined) {
  const locationId = useMatch('/locations/:id')?.params.id;
  const customerId = useMatch('/customers/:id')?.params.id;

  const { data } = useQuery({
    queryKey: ['admin-shell-brand', locationId ?? null, customerId ?? null],
    enabled: Boolean(locationId || customerId),
    staleTime: 5 * 60 * 1000,
    queryFn: async (): Promise<ShellLocation[]> => {
      let query = supabase.from('locations').select('id, name, hierarchy').is('deleted_at', null);
      query = locationId ? query.eq('id', locationId) : query.eq('customer_id', customerId!);
      const { data: locs, error } = await query;
      if (error) throw error;
      const rows = locs ?? [];
      const codes = [...new Set(rows.map((l) => propertyCodeOf(l.hierarchy)).filter((c): c is string => !!c))];
      const brandByCode = new Map<string, string | null>();
      if (codes.length > 0) {
        const { data: dirs, error: dirError } = await supabase.from('property_directory').select('code, brand').in('code', codes);
        if (dirError) throw dirError;
        for (const d of dirs ?? []) brandByCode.set(d.code, d.brand);
      }
      return rows.map((l) => {
        const code = propertyCodeOf(l.hierarchy);
        return { id: l.id, name: l.name, directory: code ? { brand: brandByCode.get(code) ?? null } : null };
      });
    },
  });

  return useMemo(
    () =>
      pickViewBrand({
        pathname,
        isPortfolio: !customerId,
        locations: data ?? [],
        activeAccountName: customerName,
      }),
    [pathname, customerId, data, customerName],
  );
}
